import { v4 as uuid } from 'uuid';
import type { ToastProps, ToastPropsBase } from '../../hooks/Toast/useToasts';
import {
  getGlobalToastsState,
  globalToastActions,
  globalToastsDispatch,
  type GlobalToastType,
} from './GlobalToastsStore';

/**
 * Module-level so onRemove still runs when add and remove use different
 * GlobalToastManager instances that share the same redux store.
 * Keyed by toastType:id so ui and appInbox never collide on the same raw id.
 */
const onRemoveById = new Map<string, (id: string) => void>();

function onRemoveKey(toastType: GlobalToastType, id: string): string {
  return `${toastType}:${id}`;
}

// Overloaded class for type safety
export class GlobalToastManager<TToastProps extends ToastPropsBase = ToastPropsBase> {
  constructor(private toastType: GlobalToastType) {}

  /**
   * Adds a toast, with a registerable type given to the class above.
   * Optional onRemove runs when the toast is dismissed (auto or manual via removeToast).
   */
  public addToast(toast: TToastProps, onRemove?: (id: string) => void): string {
    if (!toast.id) {
      toast.id = uuid();
    }

    if (onRemove) {
      onRemoveById.set(onRemoveKey(this.toastType, toast.id), onRemove);
    }

    if (this.toastType === 'ui') {
      // TODO: Typing could likely still be improved here
      const toastValue = toast as unknown as ToastProps & { id: string };
      globalToastsDispatch(
        globalToastActions.addToast({
          type: 'ui',
          value: {
            dismissable: true,
            timeout: toastValue.variant === 'NEGATIVE' ? 5000 : 3000,
            ...toastValue,
          },
        })
      );
    } else {
      globalToastsDispatch(
        globalToastActions.addToast({
          type: 'appInbox',
          value: {
            dismissable: true,
            // fallback timeout - primary timeouts provided in AppNotificationsMonitor
            timeout: 15_000,
            ...toast,
          },
        })
      );
    }
    return toast.id!;
  }

  public removeToast(id: string): void {
    const key = onRemoveKey(this.toastType, id);
    const onRemove = onRemoveById.get(key);
    onRemoveById.delete(key);
    globalToastsDispatch(globalToastActions.remove({ type: this.toastType, id }));
    onRemove?.(id);
  }

  public clearToasts(): void {
    const toasts =
      this.toastType === 'ui' ? getGlobalToastsState().toasts.uiToasts : getGlobalToastsState().toasts.appInboxToasts;
    for (const toast of toasts) {
      if (toast.id) {
        onRemoveById.delete(onRemoveKey(this.toastType, toast.id));
      }
    }
    globalToastsDispatch(globalToastActions.clear({ type: this.toastType }));
  }
}
